import { env } from '../env';
import { orchestratorAuthHeaders } from './orchestratorAuth';

/**
 * Template QA talks to the orchestrator. Control endpoints (start/status/runs)
 * go through the orchestrator with the signed-in user's Firebase ID token.
 *
 * Gallery images can't carry an Authorization header (they load via <img>), so
 * in production they're served from the public QA artifact bucket (screenshots
 * of template previews only — no client data). In dev they come from the local
 * orchestrator gallery.
 */
const QA_BASE = `${env.orchestratorUrl}/qa/template-matrix`;
const GALLERY_BASE = `${QA_BASE}/gallery`;

/** Public GCS base for finished-run artifacts (prod). Empty in dev. */
const PUBLIC_BUCKET_URL =
  import.meta.env.VITE_QA_PUBLIC_BUCKET_URL ??
  (import.meta.env.PROD
    ? 'https://storage.googleapis.com/flashpoint-61a55-qa-artifacts'
    : '');

/** Base URL for a run's static artifacts (images + manifest). */
function runArtifactBase(runId: string): string {
  if (PUBLIC_BUCKET_URL) return `${PUBLIC_BUCKET_URL}/runs/${encodeURIComponent(runId)}`;
  return `${GALLERY_BASE}/runs/${encodeURIComponent(runId)}`;
}

export interface TemplateQaManifestEntry {
  shell: string;
  vibe: string;
  slug: string;
  file?: string;
  ms: number;
  ok: boolean;
  error?: string;
  industry?: string;
  pageCount?: number;
  path?: string;
  lookVariant?: number;
  label?: string;
}

export interface TemplateQaCurrentCombo {
  shell: string;
  vibe: string;
  industry?: string;
  pageCount?: number;
  path?: string;
  lookVariant?: number;
  label?: string;
}

export interface TemplateQaProgressFile {
  runId?: string;
  status?: 'running' | 'done' | 'done_with_failures' | 'failed' | 'starting';
  current?: number;
  total?: number;
  shell?: string;
  vibe?: string;
  ok?: number;
  fail?: number;
  passed?: number;
  failed?: number;
  smoke?: boolean;
  currentCombo?: TemplateQaCurrentCombo | null;
  manifest?: TemplateQaManifestEntry[];
  startedAt?: string;
  finishedAt?: string;
  message?: string;
}

export interface TemplateQaRunSummary {
  runId: string;
  smoke?: boolean;
  status?: string;
  total?: number;
  ok?: number;
  fail?: number;
  startedAt?: string;
  finishedAt?: string;
}

export type TemplateRemediationStatus = 'todo' | 'in_progress' | 'verified';

export interface TemplateRemediationTemplate {
  template: string;
  status: TemplateRemediationStatus;
}

export interface TemplateRemediationState {
  order: string[];
  templates: TemplateRemediationTemplate[];
  nextTemplate: string | null;
  totalTemplates: number;
  completedTemplates: number;
}

export interface TemplateQaHealth {
  ok: boolean;
  checkedAt: string;
  orchestrator: { up: boolean; nodeVersion: string; port: number };
  script: { path: string; exists: boolean };
  browser: { chromiumInstalled: boolean };
  previews: { dir: string; exists: boolean; staleTopLevelPng: number };
  runs: {
    count: number;
    activeRunId: string | null;
    running: boolean;
    lastRun: TemplateQaRunSummary | null;
    lastRunFiles: {
      exists: boolean;
      pngCount: number;
      hasManifest: boolean;
      hasIndexHtml: boolean;
      bytes: number;
      dir: string;
    } | null;
  };
  live: {
    runId: string | null;
    status: string | null;
    current: number | null;
    total: number | null;
    ok: number | null;
    fail: number | null;
    currentCombo: TemplateQaCurrentCombo | null;
  } | null;
}

export interface TemplateQaStatusResponse {
  available: boolean;
  running: boolean;
  activeRunId: string | null;
  progress: TemplateQaProgressFile | null;
  galleryUrl: string;
  quotaNote: string;
  previewsDir: string;
}

export function comboDisplayLabel(
  c:
    | Pick<TemplateQaManifestEntry, 'label' | 'shell' | 'vibe' | 'industry' | 'path'>
    | TemplateQaCurrentCombo
    | null
    | undefined,
): string {
  if (!c) return '';
  if (c.label) return c.label;
  if (c.industry && c.path) return `${c.shell} · ${c.path} · ${c.industry}`;
  if (c.shell && c.vibe) return `${c.shell} / ${c.vibe}`;
  return c.shell ?? '';
}

/** Normalize qa-progress.json counts (script uses ok/fail). */
export function progressCounts(p: TemplateQaProgressFile | null): { passed: number; failed: number } {
  if (!p) return { passed: 0, failed: 0 };
  return {
    passed: p.passed ?? p.ok ?? 0,
    failed: p.failed ?? p.fail ?? 0,
  };
}

/** Live manifest from the in-flight progress file (empty until first combo). */
export function manifestFromProgress(p: TemplateQaProgressFile | null): TemplateQaManifestEntry[] {
  if (p?.manifest && p.manifest.length > 0) return p.manifest;
  return [];
}

function authHeaders(extra?: HeadersInit): Promise<HeadersInit> {
  return orchestratorAuthHeaders(extra);
}

function orchestratorReachableHint(): string {
  return ' Cannot reach the orchestrator. Start the stack with pnpm dev (orchestrator on :8787).';
}

export function templateQaGalleryUrl(): string {
  return `${GALLERY_BASE}/`;
}

/** Per-run PNG/asset URL with cache-busting so re-runs never show cached images. */
export function templateQaImageUrl(runId: string, file: string, version?: string): string {
  const v = version ? `?v=${encodeURIComponent(version)}` : '';
  return `${runArtifactBase(runId)}/${file}${v}`;
}

export async function fetchTemplateQaStatus(): Promise<TemplateQaStatusResponse> {
  let res: Response;
  try {
    res = await fetch(QA_BASE, { headers: await authHeaders() });
  } catch {
    throw new Error(`Template QA status failed.${orchestratorReachableHint()}`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Template QA status failed (${res.status}): ${text || res.statusText}`);
  }
  return (await res.json()) as TemplateQaStatusResponse;
}

export async function startTemplateQa(opts: {
  smoke?: boolean;
  shells?: string[];
  vibes?: string[];
}): Promise<{ runId: string; total?: number }> {
  let res: Response;
  try {
    res = await fetch(QA_BASE, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        smoke: Boolean(opts.smoke),
        shells: opts.shells,
        vibes: opts.vibes,
      }),
    });
  } catch {
    throw new Error(`Could not start template QA.${orchestratorReachableHint()}`);
  }
  const body = (await res.json().catch(() => ({}))) as {
    runId?: string;
    total?: number;
    message?: string;
    error?: string;
  };
  if (!res.ok) {
    const detail = body.message ?? body.error ?? res.statusText;
    throw new Error(`Template QA start failed (${res.status}): ${detail}`);
  }
  if (!body.runId) {
    throw new Error('Template QA start failed: missing runId in response.');
  }
  return { runId: body.runId, total: body.total };
}

export async function fetchTemplateQaRemediation(): Promise<TemplateRemediationState> {
  const url = `${QA_BASE}/remediation`;
  let res: Response;
  try {
    res = await fetch(url, { headers: await authHeaders(), cache: 'no-store' });
  } catch {
    throw new Error(`Template remediation status failed.${orchestratorReachableHint()}`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Template remediation status failed (${res.status}): ${text || res.statusText}`);
  }
  return (await res.json()) as TemplateRemediationState;
}

export async function setTemplateRemediationStatus(
  template: string,
  status: TemplateRemediationStatus,
): Promise<void> {
  const url = `${QA_BASE}/remediation/${encodeURIComponent(template)}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'PATCH',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ status }),
    });
  } catch {
    throw new Error(`Could not update template status.${orchestratorReachableHint()}`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Template status update failed (${res.status}): ${text || res.statusText}`);
  }
}

export async function startTemplateRemediationRun(
  template: string,
  vibes?: string[],
): Promise<{ runId: string; total?: number }> {
  const url = `${QA_BASE}/remediation/${encodeURIComponent(template)}/run`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ vibes }),
    });
  } catch {
    throw new Error(`Could not start template remediation run.${orchestratorReachableHint()}`);
  }
  const body = (await res.json().catch(() => ({}))) as {
    runId?: string;
    total?: number;
    message?: string;
    error?: string;
  };
  if (!res.ok) {
    const detail = body.message ?? body.error ?? res.statusText;
    throw new Error(`Template remediation run failed (${res.status}): ${detail}`);
  }
  if (!body.runId) throw new Error('Template remediation run failed: missing runId in response.');
  return { runId: body.runId, total: body.total };
}

export async function fetchTemplateQaRun(runId: string): Promise<{
  runId: string;
  orchestratorStatus: string;
  progress: TemplateQaProgressFile | null;
}> {
  const url = `${QA_BASE}/${encodeURIComponent(runId)}`;
  let res: Response;
  try {
    res = await fetch(url, { headers: await authHeaders() });
  } catch {
    throw new Error(`Template QA poll failed.${orchestratorReachableHint()}`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Template QA poll failed (${res.status}): ${text || res.statusText}`);
  }
  return (await res.json()) as {
    runId: string;
    orchestratorStatus: string;
    progress: TemplateQaProgressFile | null;
  };
}

function fileNameFromContentDisposition(v: string | null, fallback: string): string {
  if (!v) return fallback;
  const m = /filename="?([^"]+)"?/i.exec(v);
  return m?.[1] ?? fallback;
}

/** Archived runs (newest first), for the history picker. */
export async function fetchTemplateQaRuns(): Promise<{
  runs: TemplateQaRunSummary[];
  activeRunId: string | null;
}> {
  const url = `${QA_BASE}/runs`;
  let res: Response;
  try {
    res = await fetch(url, { headers: await authHeaders() });
  } catch {
    throw new Error(`Template QA runs failed.${orchestratorReachableHint()}`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Template QA runs failed (${res.status}): ${text || res.statusText}`);
  }
  const body = (await res.json()) as { runs?: TemplateQaRunSummary[]; activeRunId?: string | null };
  return { runs: body.runs ?? [], activeRunId: body.activeRunId ?? null };
}

/** Orchestrator + tool health snapshot for the dashboard status panel. */
export async function fetchTemplateQaHealth(): Promise<TemplateQaHealth> {
  const url = `${QA_BASE}/health`;
  let res: Response;
  try {
    res = await fetch(url, { headers: await authHeaders(), cache: 'no-store' });
  } catch {
    throw new Error(`Template QA health failed.${orchestratorReachableHint()}`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Template QA health failed (${res.status}): ${text || res.statusText}`);
  }
  return (await res.json()) as TemplateQaHealth;
}

/** Open the right gallery: the selected run's page if known, else the top-level. */
export function templateQaRunGalleryUrl(runId?: string | null): string {
  if (runId) return `${runArtifactBase(runId)}/index.html`;
  return `${GALLERY_BASE}/`;
}

export interface RunManifestResult {
  entries: TemplateQaManifestEntry[];
  /** Non-null when the manifest could not be loaded (network, 404, parse). */
  error: string | null;
}

/**
 * Load a finished run's manifest from the gallery (no auth needed). Surfaces
 * load failures so the dashboard can show why a run is empty instead of a
 * silent "No screenshots".
 */
export async function fetchRunManifestResult(runId: string): Promise<RunManifestResult> {
  const url = `${runArtifactBase(runId)}/manifest.json`;
  let res: Response;
  try {
    res = await fetch(url, { cache: 'no-store' });
  } catch {
    return { entries: [], error: `Could not reach the gallery.${orchestratorReachableHint()}` };
  }
  if (!res.ok) {
    return {
      entries: [],
      error:
        res.status === 404
          ? 'Manifest not found for this run (it may have been cleared or is mid-write).'
          : `Manifest load failed (${res.status}).`,
    };
  }
  try {
    const entries = (await res.json()) as TemplateQaManifestEntry[];
    return { entries, error: null };
  } catch {
    return {
      entries: [],
      error:
        'Manifest did not parse as JSON. If you just changed vite.config.ts, restart pnpm dev so the /qa proxy is active.',
    };
  }
}

/** Backward-compatible manifest loader (entries only). */
export async function fetchRunManifest(runId: string): Promise<TemplateQaManifestEntry[]> {
  return (await fetchRunManifestResult(runId)).entries;
}

/** Clear the live view. Keeps archived runs + PNGs on disk. */
export async function clearTemplateQa(): Promise<void> {
  let res: Response;
  try {
    res = await fetch(QA_BASE, { method: 'DELETE', headers: await authHeaders() });
  } catch {
    throw new Error(`Could not clear template QA.${orchestratorReachableHint()}`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Clear failed (${res.status}): ${text || res.statusText}`);
  }
}

/** Download screenshots + run metadata as one zip for issue handoff. */
export async function downloadTemplateQaRunBundle(runId: string): Promise<{ fileName: string }> {
  const url = `${QA_BASE}/${encodeURIComponent(runId)}/export`;
  let res: Response;
  try {
    res = await fetch(url, { headers: await authHeaders() });
  } catch {
    throw new Error(`Could not download run bundle.${orchestratorReachableHint()}`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Bundle download failed (${res.status}): ${text || res.statusText}`);
  }
  const blob = await res.blob();
  const fileName = fileNameFromContentDisposition(
    res.headers.get('Content-Disposition'),
    `${runId}-bundle.zip`,
  );
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return { fileName };
}
